import React from 'react';
import {
    createBrowserRouter, createRoutesFromElements, RouterProvider, Route, Navigate,
} from 'react-router-dom';
import { useAuthStore } from './store/useStore';
import { GuardiaUscita } from './utils/uscitaProtetta';
import Login from './pages/Login';
import CambiaPassword from './pages/CambiaPassword';
import Profilo from './pages/Profilo';
import PasswordDimenticata from './pages/PasswordDimenticata';
import ReimpostaPassword from './pages/ReimpostaPassword';
import Dashboard from './pages/Dashboard';
import Layout from './components/Layout';
import PazientiList from './components/pazienti/PazientiList';
import PazienteDetail from './components/pazienti/PazienteDetail';
import MediciList from './components/medici/MediciList';
import PrestazioniList from './components/prestazioni/PrestazioniList';
import PacchettiSpeciali from './pages/PacchettiSpeciali';
import ConsensiDaReinformare from './pages/ConsensiDaReinformare';
import ModelliConsenso from './pages/ModelliConsenso';
import Spettanze from './components/Spettanze';
import GestioneUtenti from './pages/GestioneUtenti';
import Cestino from './pages/Cestino';
import Promemoria from './pages/Promemoria';
import Magazzino from './pages/Magazzino';
import AreaDipendenti from './pages/AreaDipendenti';
import AccettazioniTablet from './pages/AccettazioniTablet';
import ApertureRiservate from './pages/ApertureRiservate';
import StatoSistema from './pages/StatoSistema';
import DatiFiscali from './pages/DatiFiscali';
import SoggettiFatturazione from './pages/SoggettiFatturazione';
import AmministrazioneAi from './pages/AmministrazioneAi';
import Amministrazione from './pages/Amministrazione';
import CatalogoCampi from './pages/CatalogoCampi';
import SchedeCliniche from './pages/SchedeCliniche';
import ValidazioneCampi from './pages/ValidazioneCampi';
import PannelloAnalisi from './pages/PannelloAnalisi';
import Acquisizione from './pages/Acquisizione';
import Dispositivi from './pages/Dispositivi';
import TestiRiutilizzabili from './pages/TestiRiutilizzabili';
import AlbiSedi from './pages/AlbiSedi';
/* Il referto si carica a parte: porta con se' l'editor, che pesa 93 kB
   compressi. Caricato con il resto li pagherebbe anche chi apre solo
   l'agenda. */
const Referto = React.lazy(() => import('./pages/Referto'));
import MieSchede from './pages/MieSchede';
import MioContoProdotti from './pages/MioContoProdotti';
import RegistroRxCentrale from './pages/RegistroRxCentrale';
import Agenda from './pages/Agenda';
import GestioneOrari from './pages/GestioneOrari';
import GestioneStanze from './pages/GestioneStanze';
import ControlloGestione from './pages/ControlloGestione';
import SistemaTS from './pages/SistemaTS';
import Fatturazione from './pages/Fatturazione';
import Compiti from './pages/Compiti';
import Tablet from './pages/Tablet';
import RegistroAccessi from './pages/RegistroAccessi';
import RegistroModifiche from './pages/RegistroModifiche';
import './styles/App.css';
import { FRONT_DESK } from './utils/permessi';

/* Il ruolo «acquisizione» - il telefono dello studio - ha una pagina
   sola e non entra nel gestionale: da qualunque rotta protetta torna
   li'. Non e' protezione (quella sta sul server, che rifiuta ogni altra
   rotta con 403): serve a non mostrare una schermata piena di errori. */
const SOLO_ACQUISIZIONE = (user) => user?.ruolo === 'acquisizione';

const ProtectedRoute = ({ children }) => {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    const user = useAuthStore((state) => state.user);
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    if (user?.deve_cambiare_password) return <Navigate to="/cambia-password" replace />;
    if (SOLO_ACQUISIZIONE(user)) return <Navigate to="/acquisizione" replace />;
    return <Layout>{children}</Layout>;
};

/* La pagina del telefono: senza Layout, senza menu. Ammessi anche admin
   e segreteria, che hanno il telefono in mano come chiunque. */
const AcquisizioneRoute = ({ children }) => {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    const user = useAuthStore((state) => state.user);
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    if (!['acquisizione', 'admin', 'segreteria'].includes(user?.ruolo)) return <Navigate to="/dashboard" replace />;
    return children;
};

// Riservata agli admin: se il ruolo non e' admin, rimanda alla dashboard.
const AdminRoute = ({ children }) => {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    const user = useAuthStore((state) => state.user);
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    if (user?.deve_cambiare_password) return <Navigate to="/cambia-password" replace />;
    if (SOLO_ACQUISIZIONE(user)) return <Navigate to="/acquisizione" replace />;
    if (user?.ruolo !== 'admin') return <Navigate to="/dashboard" replace />;
    return <Layout>{children}</Layout>;
};

// Riservata a un elenco di ruoli. Nascondere la voce di menu non basta:
// queste schermate erano sotto ProtectedRoute e un medico che digitava
// l'indirizzo ci arrivava, e poi vedeva una pagina vuota o un errore
// tecnico perche' il backend rifiutava le chiamate. Chi non ha il ruolo
// giusto torna alla dashboard, come per le rotte admin.
const RuoloRoute = ({ ruoli, children }) => {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    const user = useAuthStore((state) => state.user);
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    if (user?.deve_cambiare_password) return <Navigate to="/cambia-password" replace />;
    if (SOLO_ACQUISIZIONE(user)) return <Navigate to="/acquisizione" replace />;
    if (!ruoli.includes(user?.ruolo)) return <Navigate to="/dashboard" replace />;
    return <Layout>{children}</Layout>;
};

// Le schermate del front-desk (cassa, fatturazione, tessera sanitaria,
// magazzino, orari, stanze): l'elenco sta in utils/permessi.js, lo stesso
// che usano i collegamenti verso queste rotte. Un medico non ci finisce
// dentro a mani vuote, e un collegamento non porta a un 403.

// Schermata di cambio password: senza Layout, cosi' non c'e' un menu
// da cui allontanarsi. Chi non deve cambiarla viene rimandato indietro.
const CambioPasswordRoute = () => {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    const user = useAuthStore((state) => state.user);
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    if (!user?.deve_cambiare_password) return <Navigate to="/dashboard" replace />;
    return <CambiaPassword />;
};

/* createBrowserRouter, non BrowserRouter: dal 21 settembre 2026 il
   router e' del tipo «con dati», perche' e' l'unico su cui funziona
   useBlocker - il «vuoi uscire senza salvare» delle schermate con un
   modulo. Le rotte sono le stesse di prima, riga per riga: le dichiara
   createRoutesFromElements con la stessa sintassi <Route>, e l'inventario
   (scripts/inventarioRotte.js, src/inventarioRotte.test.js) e' stato
   confrontato prima e dopo il passaggio: zero differenze.

   Il router si costruisce UNA volta, fuori dal componente: ricostruirlo
   a ogni render di App vorrebbe dire una cronologia nuova ogni volta. */
const router = createBrowserRouter(
    createRoutesFromElements(
        /* GuardiaUscita e' l'unico blocker dell'applicazione (il router ne
           ammette uno): sta qui, sulla radice, e rende le rotte figlie. */
        <Route element={<GuardiaUscita />}>
                <Route path="/login" element={<Login />} />
                {/* Tablet in sala d'attesa: nessun utente collegato,
                    si identifica con la chiave del dispositivo */}
                <Route path="/tablet" element={<Tablet />} />
                <Route path="/cambia-password" element={<CambioPasswordRoute />} />
                {/* Pubbliche: chi ha perso la password non ha una sessione. */}
                <Route path="/password-dimenticata" element={<PasswordDimenticata />} />
                <Route path="/reimposta-password" element={<ReimpostaPassword />} />
                {/* Il profilo lo raggiungono tutti i ruoli: e' anche
                    l'unica schermata aperta a chi e' obbligato al
                    secondo fattore e non l'ha ancora attivato. */}
                <Route path="/profilo" element={<ProtectedRoute><Profilo /></ProtectedRoute>} />
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/pazienti" element={<ProtectedRoute><PazientiList /></ProtectedRoute>} />
                <Route path="/pazienti/:id" element={<ProtectedRoute><PazienteDetail /></ProtectedRoute>} />
                <Route path="/medici" element={<ProtectedRoute><MediciList /></ProtectedRoute>} />
                <Route path="/prestazioni" element={<ProtectedRoute><PrestazioniList /></ProtectedRoute>} />
                {/* I pacchetti speciali si guardano, non si fatturano: la
                    schermata e' del front-desk come le altre voci di cassa.
                    Il catalogo resta aperto a tutti, questa no. */}
                <Route path="/pacchetti-speciali" element={<RuoloRoute ruoli={FRONT_DESK}><PacchettiSpeciali /></RuoloRoute>} />
                {/* Cicli aperti su una versione di consenso precedente. Aperta a
                    tutti gli autenticati: la segreteria richiama i pazienti e fissa
                    le sedute, e sapere che a quello il medico deve dire una cosa
                    prima di cominciare e' parte del suo lavoro. Annotare invece lo
                    puo' solo un medico, e a impedirlo e' il server. */}
                <Route path="/consensi-da-reinformare" element={<ProtectedRoute><ConsensiDaReinformare /></ProtectedRoute>} />
                {/* I modelli di consenso. Aperta a tutti gli autenticati come
                    le altre rotte dei modelli: chi apre un modello di un
                    collega lo ferma il controller, non il routing. */}
                <Route path="/modelli-consenso" element={<ProtectedRoute><ModelliConsenso /></ProtectedRoute>} />
                {/* La libreria dei testi riutilizzabili: aperta a tutti gli
                    autenticati, il server decide chi vede e scrive cosa. */}
                <Route path="/testi" element={<ProtectedRoute><TestiRiutilizzabili /></ProtectedRoute>} />
                <Route path="/magazzino" element={<RuoloRoute ruoli={FRONT_DESK}><Magazzino /></RuoloRoute>} />
                {/* Area dipendenti: la apre chiunque abbia un account, perche'
                    ognuno ci trova le PROPRIE cose. Il confine fra "le mie" e
                    "quelle di tutti" lo fa il server filtrando per identita',
                    non questa rotta filtrando per ruolo. */}
                <Route path="/area-dipendenti" element={<ProtectedRoute><AreaDipendenti /></ProtectedRoute>} />
                <Route path="/accettazioni" element={<RuoloRoute ruoli={FRONT_DESK}><AccettazioniTablet /></RuoloRoute>} />
                {/* Chi ha aperto una procedura riservata. La vedono i medici
                    (le proprie) e l'amministrazione (tutte): chi filtra e'
                    il server, non questa riga. */}
                <Route path="/aperture-riservate" element={<ProtectedRoute><ApertureRiservate /></ProtectedRoute>} />
                <Route path="/soggetti" element={<RuoloRoute ruoli={FRONT_DESK}><SoggettiFatturazione /></RuoloRoute>} />
                {/* Come sta il server: backup, lavori notturni, scadenze,
                    allarmi. SOLO ADMIN, e lo fa rispettare anche il server
                    (`authorize('admin')` sulla rotta): questa pagina dice
                    dove stanno i backup e quando scadono i certificati. */}
                <Route path="/stato-sistema" element={<AdminRoute><StatoSistema /></AdminRoute>} />
                {/* I dati della societa' per la fattura elettronica (blocco 6): solo admin, come la rotta. */}
                <Route path="/dati-fiscali" element={<AdminRoute><DatiFiscali /></AdminRoute>} />
                <Route path="/assistenza-admin" element={<AdminRoute><AmministrazioneAi /></AdminRoute>} />
                <Route path="/amministrazione" element={<AdminRoute><Amministrazione /></AdminRoute>} />
                {/* Il catalogo dei campi clinici lo cambia solo l'amministrazione.
                    Le schede le compone l'amministrazione, ma la segreteria puo'
                    attivarle e ordinarle: e' il server a separare le due cose, qui
                    si allinea il routing perche' nessuno ci finisca dentro a mani vuote. */}
                <Route path="/campi" element={<AdminRoute><CatalogoCampi /></AdminRoute>} />
                <Route path="/schede-cliniche" element={<RuoloRoute ruoli={FRONT_DESK}><SchedeCliniche /></RuoloRoute>} />
                {/* La leggono tutti, medici compresi: un medico deve poter
                    vedere che cosa gli e' stato chiesto di leggere. Scrivere
                    e' un'altra cosa, e lo decidono le rotte del server. */}
                <Route path="/validazione-campi" element={<ProtectedRoute><ValidazioneCampi /></ProtectedRoute>} />
                <Route path="/referti/:id" element={
                    <ProtectedRoute>
                        <React.Suspense fallback={<div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
                            Apertura del referto…</div>}>
                            <Referto />
                        </React.Suspense>
                    </ProtectedRoute>} />
                <Route path="/mie-schede" element={<RuoloRoute ruoli={['medico', 'medico_senior']}><MieSchede /></RuoloRoute>} />
                {/* Il conto prodotti del medico. La linguetta dei Conti
                    medici sta dentro il Magazzino, che ai medici e' chiuso:
                    senza questa rotta il permesso «ciascuno vede il proprio
                    conto» non avrebbe nessuna porta. */}
                <Route path="/mio-conto-prodotti" element={<RuoloRoute ruoli={['medico', 'medico_senior']}><MioContoProdotti /></RuoloRoute>} />
                {/* Registro RX, elenco centrale. Solo front-desk: un medico le
                    proprie righe le trova sulla scheda del paziente, e questa
                    schermata esiste per il conto fra struttura e professionisti. */}
                <Route path="/registro-rx" element={<RuoloRoute ruoli={FRONT_DESK}><RegistroRxCentrale /></RuoloRoute>} />
                <Route path="/agenda" element={<ProtectedRoute><Agenda /></ProtectedRoute>} />
                <Route path="/orari" element={<RuoloRoute ruoli={FRONT_DESK}><GestioneOrari /></RuoloRoute>} />
                <Route path="/stanze" element={<RuoloRoute ruoli={FRONT_DESK}><GestioneStanze /></RuoloRoute>} />
                <Route path="/controllo-gestione" element={<AdminRoute><ControlloGestione /></AdminRoute>} />
                {/* Solo amministrazione, come il controllo di gestione: il
                    margine per specialita' dice quanto la struttura guadagna
                    su ogni branca, e non e' informazione da front-desk. Il
                    controllo vero sta sul server, questa e' solo la porta. */}
                <Route path="/analisi" element={<AdminRoute><PannelloAnalisi /></AdminRoute>} />
                <Route path="/acquisizione" element={<AcquisizioneRoute><Acquisizione /></AcquisizioneRoute>} />
                {/* «Tablet»: dispositivi (creazione e chiave solo admin) e
                    compilazioni in sospeso (anche segreteria, che sta al
                    banco quando un tablet si ferma). */}
                <Route path="/dispositivi" element={<RuoloRoute ruoli={FRONT_DESK}><Dispositivi /></RuoloRoute>} />
                {/* Le sedi degli albi le aggiunge solo l'amministrazione. */}
                <Route path="/albi" element={<AdminRoute><AlbiSedi /></AdminRoute>} />
                <Route path="/sistema-ts" element={<RuoloRoute ruoli={FRONT_DESK}><SistemaTS /></RuoloRoute>} />
                <Route path="/fatturazione" element={<RuoloRoute ruoli={FRONT_DESK}><Fatturazione /></RuoloRoute>} />
                <Route path="/compiti" element={<ProtectedRoute><Compiti /></ProtectedRoute>} />
                <Route path="/spettanze" element={<ProtectedRoute><Spettanze /></ProtectedRoute>} />
                <Route path="/promemoria" element={<ProtectedRoute><Promemoria /></ProtectedRoute>} />
                <Route path="/utenti" element={<RuoloRoute ruoli={FRONT_DESK}><GestioneUtenti /></RuoloRoute>} />
                <Route path="/cestino" element={<AdminRoute><Cestino /></AdminRoute>} />
                <Route path="/registro-accessi" element={<AdminRoute><RegistroAccessi /></AdminRoute>} />
                <Route path="/registro-modifiche" element={<AdminRoute><RegistroModifiche /></AdminRoute>} />
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
    )
);

function App() {
    return <RouterProvider router={router} />;
}

export default App;
